import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  InputAdornment,
  useTheme,
  Alert,
} from "@mui/material";
import {
  Email,
  ArrowBack,
  VpnKey,
} from "@mui/icons-material";
import axios from "axios";
import { motion } from "framer-motion";
import Swal from "sweetalert2";

const ForgotPassword = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const URL = "https://tdtlworld.com/dss-kaas-backend/";

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        if (!email) {
            setError("Please enter your email address.");
            return;
        }

        setLoading(true);
        setError("");
        try {
            const response = await axios.post(`${URL}auth/forgot-password/`, {
                email: email
            });
            
            Swal.fire({
                icon: "success",
                title: "OTP Sent",
                text: response.data.message || "An OTP has been sent to your email address.",
                timer: 2000,
                showConfirmButton: false,
            });
            
            sessionStorage.setItem("OTP_duration", response.data.OTP_duration);
            navigate("/db-shiksha/kaas/student/resetPassword");
        } catch (err) {
            console.error("There was an error!", err);
            setError(err.response?.data?.message || err.response?.data?.error || "Failed to send OTP. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                minHeight: "100vh",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "background.default",
                p: 2,
            }}
        >
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Container maxWidth="xs">
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 3, md: 5 },
                            borderRadius: "32px",
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: "background.paper",
                            textAlign: "center",
                        }}
                    >
                        <IconButton 
                            onClick={() => navigate(-1)} 
                            sx={{ mb: 2, alignSelf: 'flex-start' }}
                        >
                            <ArrowBack />
                        </IconButton>
                        
                        <Box sx={{ mb: 4 }}>
                            <VpnKey sx={{ fontSize: 60, color: "#D4AF37", mb: 2 }} />
                            <Typography variant="h4" fontWeight={800} gutterBottom>
                                Forgot Password
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                No worries! Enter your email below and we'll send you an OTP to reset your password.
                            </Typography>
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 3, borderRadius: "12px", textAlign: 'left' }}>
                                {error}
                            </Alert>
                        )}

                        <form onSubmit={handleForgotPassword}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                variant="outlined"
                                margin="normal"
                                required
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email sx={{ color: "#D4AF37" }} />
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: "12px" }
                                }}
                            />

                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                type="submit"
                                disabled={loading}
                                sx={{
                                    mt: 4,
                                    py: 1.5,
                                    borderRadius: "50px",
                                    fontWeight: 800,
                                    fontSize: "1.1rem",
                                    boxShadow: "0 8px 16px rgba(212, 175, 55, 0.2)",
                                }}
                            >
                                {loading ? "Sending..." : "Send OTP"}
                            </Button>

                            <Box sx={{ mt: 3 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Remember your password?{" "}
                                    <Link 
                                        to="/db-shiksha/kaas/login" 
                                        style={{ color: "#D4AF37", fontWeight: 700, textDecoration: 'none' }}
                                    >
                                        Log In
                                    </Link>
                                </Typography>
                            </Box>
                        </form>
                    </Paper>
                </Container>
            </motion.div>
        </Box>
    );
};

export default ForgotPassword;
